import { css } from '@emotion/react';
import { semantic_colors, shapes, spacing } from '../../foundation';
import { getAutoColorCode } from '../../styles';

const selected_background_color = '#E7EAEB';
const selected_hover_background_color = '#DFE2E3';
const selected_pressed_background_color = '#D7DADB';

export const menu_base_css = css`
  margin: 0;
  padding: 0;
  list-style: none;
`;

export const menu_list_gap_css = css`
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

export const menu_item_base_css = css`
  appearance: none;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  width: calc(100% - 16px);
  margin: 0 8px;
  border: 0;
  ${shapes.border_radius.small}
  background-color: transparent;
  color: ${semantic_colors.content.primary};
  text-decoration: none;
  gap: 6px;
  transition: all 0.1s linear;
  cursor: pointer;

  &[aria-current='page'] {
    background-color: ${selected_background_color};
  }

  &:hover {
    background-color: ${getAutoColorCode(semantic_colors.fill.tertiary).hover};
  }

  &:focus {
    outline: none;
  }

  &:focus-visible,
  &:active {
    background-color: ${getAutoColorCode(semantic_colors.fill.tertiary).pressed};
  }

  &[aria-current='page']:hover {
    background-color: ${selected_hover_background_color};
  }

  &[aria-current='page']:focus-visible,
  &[aria-current='page']:active {
    background-color: ${selected_pressed_background_color};
  }
`;

/** MenuGroup 헤더는 선택·열림 상태에서 배경을 유지하지 않고 hover/pressed 에서만 바뀐다. */
export const menu_group_header_css = css`
  &[aria-current='page'] {
    background-color: transparent;
  }

  &[aria-current='page']:hover {
    background-color: ${getAutoColorCode(semantic_colors.fill.tertiary).hover};
  }

  &[aria-current='page']:focus-visible,
  &[aria-current='page']:active {
    background-color: ${getAutoColorCode(semantic_colors.fill.tertiary).pressed};
  }
`;

export const menu_group_item_css = css`
  ${menu_item_base_css};
`;

export const menu_group_container_css = css`
  box-sizing: border-box;
  overflow: hidden;
`;

export const menu_group_list_css = css`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  padding: ${spacing.spacing_1} 0 ${spacing.spacing_2};
`;

export const menu_item_content_css = css`
  min-width: 0;
  flex: 1;
`;

export const menu_item_label_css = css`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const menu_item_icon_css = css`
  display: inline-flex;
  flex: none;

  > svg {
    width: 16px;
    height: 16px;
  }
`;

export const menu_item_right_content_css = css`
  display: inline-flex;
  align-items: center;
  flex: none;
`;

export const menu_group_chevron_css = css`
  display: inline-flex;
  flex: none;

  > svg {
    width: 14px;
    height: 14px;
  }
`;

export const menu_section_css = css`
  box-sizing: border-box;
  /* 부모 목록의 1px gap 을 포함해 시안의 섹션 간격 12px 이 되도록 뺀다. */
  padding-top: calc(${spacing.spacing_3} - 1px);

  &:first-of-type {
    padding-top: 0;
  }
`;

export const menu_section_label_css = css`
  display: flex;
  align-items: center;
  box-sizing: border-box;
  min-height: 18px;
  padding: 0 20px 1px;
  /* 시안의 라벨 텍스트 박스가 18px 다. 부모 line-height 가 끼어들지 않게 고정한다. */
  line-height: 18px;
`;

export const menu_section_sub_label_css = css`
  padding-bottom: ${spacing.spacing_1};
`;

export const menu_section_divider_css = css`
  min-width: 0;
  flex: 1;
`;

export const menu_section_list_css = css`
  ${menu_list_gap_css};
  margin: 0;
  padding: 0;
  list-style: none;
`;

export const menu_group_section_css = css`
  display: flex;
  flex-direction: column;
  padding-top: 0;

  &:not(:first-child) {
    margin-top: ${spacing.spacing_3};
  }
`;

export const menu_group_section_label_css = css`
  display: flex;
  align-items: center;
  box-sizing: border-box;
  width: calc(100% - 16px);
  min-height: 22px;
  margin: 0 8px;
  padding: 0 16px 4px 34px;
  gap: ${spacing.spacing_2};
`;

export const menu_group_section_list_css = css`
  display: flex;
  flex-direction: column;
  /* menu_section_list_css 의 gap 을 되돌린다. */
  gap: 0;
`;

export function getMenuGroupStyles(dense: boolean) {
  return dense
    ? {
        padding: '5px 8px 5px 34px',
      }
    : {
        height: 53,
        padding: '16px 32px 16px 48px',
      };
}

export function getMenuSubItemStyles(dense: boolean) {
  return dense
    ? {
        padding: '4px 12px',
      }
    : {
        height: 45,
        padding: '12px 20px',
      };
}

export function getMenuItemStyles(dense: boolean) {
  return dense
    ? {
        padding: '7px 12px',
      }
    : {
        height: 62,
        padding: '20px',
      };
}
